import { v4 as uuidv4 } from "uuid"
import {
  ROUTINE_CLIENTE_PRECOS_LOG_WORKFLOW_PATH,
  ROUTINE_CURVAS_WORKFLOW_PATH,
  ROUTINE_CURVAS_V2_WORKFLOW,
  ROUTINE_LIQUIDEZ_WORKFLOW_PATH,
  ROUTINE_PRECOS_ANDADOS_WORKFLOW_PATH,
  ROUTINE_PRECOS_EVENTOS_FINANCEIROS_WORKFLOW_PATH,
  ROUTINE_PRECOS_EVENTOS_LISTA_WORKFLOW_PATH,
  ROUTINE_PRECOS_LISTA_CETIPS_WORKFLOW_PATH,
  ROUTINE_PRECOS_PROCESSADOS_WORKFLOW_PATH,
  ROUTINE_PRECOS_TUNEL_WORKFLOW_PATH,
  ROUTINE_PRECOS_VALIDACAO_WORKFLOW_PATH,
  ROUTINE_REBUILD_INDEX_CURVAS_PATH,
  ROUTINE_RELATORIO_TABELA_CALCULO_PATH,
  ROUTINE_RISKFREE_WORKFLOW_PATH,
  ROUTINE_SPREADS_BRUTOS_TRATADOS_WORKFLOW_PATH,
  ROUTINE_SPREADS_NEGOCIADOS_CONSOLIDADOS_WORKFLOW_PATH,
  ROUTINE_SPREADS_NEGOCIADOS_WORKFLOW_PATH,
  ROUTINE_SPREADSEMISSAO_WORKFLOW_PATH,
  ROUTINE_VOLATILIDADE_WORKFLOW_V3_PATH,
  WORKFLOW_EXECUTIONS_V2_PATH,
} from "src/utils/api-routes"
import { ServiceRequest } from "src/utils/service-request"

export const pageToOffset = (page = 1, pageSize = 25) =>
  (Math.max(1, Number(page) || 1) - 1) * Math.max(1, Number(pageSize) || 25)

export const normalizePaginatedResponse = (response) => {
  const body = response?.body ?? response?.data ?? {}
  const items = Array.isArray(body.results) ? body.results : []
  return {
    items,
    total: Math.max(0, Number(body.count ?? items.length) || 0),
    next: body.next ?? null,
    previous: body.previous ?? null,
  }
}

const compactQuery = (params) => {
  const query = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      query.set(key, value)
    }
  })
  return query.toString()
}

export class RoutineService {
  constructor() {
    this.request = new ServiceRequest()
    this.pendingPricing = null
  }

  getAPPList() {
    return [
      { value: "json", description: "json" },
      { value: "csv", description: "csv" },
    ]
  }

  async startRoutine(path, body) {
    return this.request.post(path, body)
  }

  async startVolatilityWorkflowV3(dataReferencia, quantidadeDias = 90) {
    const days = quantidadeDias === "" ? 90 : Number(quantidadeDias)
    if (!Number.isInteger(days) || days < 1) {
      throw new Error("Informe uma quantidade inteira positiva de dias")
    }
    let response
    try {
      response = await this.request.post(ROUTINE_VOLATILIDADE_WORKFLOW_V3_PATH, {
        data_referencia: dataReferencia,
        quantidade_dias: days,
      }, true)
    } catch (error) {
      if (error.status === 401) {
        this.request.verifyStatusError("POST", ROUTINE_VOLATILIDADE_WORKFLOW_V3_PATH, error)
      }
      throw error
    }
    const result = response?.body
    if (result?.status !== "accepted" || typeof result.run_id !== "string" || !result.run_id.trim()) {
      throw new Error("Não foi possível confirmar o aceite da execução de volatilidade")
    }
    return result
  }

  async startTratamentoRiskFreeV2(dataReferencia) {
    return this.request.post(
      ROUTINE_RISKFREE_WORKFLOW_PATH,
      { data_referencia: dataReferencia },
      true
    )
  }

  async startRebuild() {
    return this.request.post(ROUTINE_REBUILD_INDEX_CURVAS_PATH)
  }

  async sendFilePrecosAndadosWorkflow(dateSelected, type, selectedFile) {
    if (!dateSelected || !type || !selectedFile) {
      throw new Error("Tipo da extensão, data de cálculo e arquivo são obrigatórios.")
    }

    const formData = new FormData()
    formData.append("data_referencia", dateSelected)
    formData.append("tipo_extensao", type)
    formData.append("arquivo", selectedFile)

    return this.request.postFormData(ROUTINE_PRECOS_ANDADOS_WORKFLOW_PATH, formData)
  }

  async startarPrecosEventosListaWorkflow(lista_cetips, data_inicial, data_final) {
    return this.request.post(ROUTINE_PRECOS_EVENTOS_LISTA_WORKFLOW_PATH, {
      lista_cetips,
      data_inicial,
      data_final,
    })
  }

  async startarPrecosListaCetipsWorkflow(lista_cetips, data_inicial, data_final) {
    return this.request.post(ROUTINE_PRECOS_LISTA_CETIPS_WORKFLOW_PATH, {
      lista_cetips,
      data_inicial,
      data_final,
    })
  }

  async startCalculatedPrices(data_inicial, data_final) {
    const signature = JSON.stringify([data_inicial, data_final])
    const storageKey = "pricing-pending-trigger"
    let pending

    try {
      pending = JSON.parse(sessionStorage.getItem(storageKey) || "null")
    } catch (_) {
      /* Storage may be unavailable. */
    }

    pending = pending?.signature === signature ? pending : this.pendingPricing
    if (pending?.signature !== signature) {
      pending = { signature, key: uuidv4() }
    }
    this.pendingPricing = pending

    try {
      sessionStorage.setItem(storageKey, JSON.stringify(pending))
    } catch (_) {
      /* Keep the in-memory key. */
    }

    const response = await this.request.post(
      "/calculo/api/v1/precos-calculados/workflow",
      {
        data_inicial,
        data_final,
        idempotency_key: pending.key,
      },
      true
    )

    if (
      !response?.body?.run_id ||
      response.body.data_inicial !== data_inicial ||
      response.body.data_final !== data_final
    ) {
      throw new Error("Não foi possível confirmar a solicitação. Tente novamente com as mesmas datas.")
    }

    try {
      sessionStorage.removeItem(storageKey)
    } catch (_) {
      /* No effect on the accepted request. */
    }

    this.pendingPricing = null
    return response
  }

  async startarPrecosProcessadosWorkflow(data_inicial, data_final) {
    return this.request.post(ROUTINE_PRECOS_PROCESSADOS_WORKFLOW_PATH, {
      data_inicial,
      data_final,
    })
  }

  async startarPrecosTunelWorkflow(data_inicial, data_final) {
    return this.request.post(ROUTINE_PRECOS_TUNEL_WORKFLOW_PATH, {
      data_inicial,
      data_final,
    })
  }

  async startarPrecosValidacaoWorkflow(lista_cetips, data_inicial, data_final) {
    return this.request.post(ROUTINE_PRECOS_VALIDACAO_WORKFLOW_PATH, {
      lista_cetips,
      data_inicial,
      data_final,
    })
  }

  async startarSpreadsBrutosTratadosWorkflow(data_referencia) {
    return this.request.post(ROUTINE_SPREADS_BRUTOS_TRATADOS_WORKFLOW_PATH, {
      data_referencia,
    })
  }

  async startarSpreadEmissaoWorkflow(data_inicial, data_final) {
    return this.request.post(ROUTINE_SPREADSEMISSAO_WORKFLOW_PATH, {
      data_inicial,
      data_final,
    })
  }

  async startarSpreadNegociadoWorkflow(data_inicial, data_final) {
    return this.request.post(ROUTINE_SPREADS_NEGOCIADOS_WORKFLOW_PATH, {
      data_inicial,
      data_final,
    })
  }

  async startarSpreadsNegociadosConsolidadosWorkflow(data_referencia) {
    return this.request.post(ROUTINE_SPREADS_NEGOCIADOS_CONSOLIDADOS_WORKFLOW_PATH, {
      data_referencia,
    })
  }

  async startarLiquidezWorkflow(data_inicial, data_final) {
    return this.request.post(ROUTINE_LIQUIDEZ_WORKFLOW_PATH, {
      data_inicial,
      data_final,
    })
  }

  async startarClientePrecosLogWorkflow(data_referencia) {
    return this.request.post(ROUTINE_CLIENTE_PRECOS_LOG_WORKFLOW_PATH, {
      data_referencia,
    })
  }

  async startarCurvasWorkflow(data_referencia) {
    return this.request.post(ROUTINE_CURVAS_WORKFLOW_PATH, {
      data_referencia,
    })
  }

  async startarCurvasV2Workflow(data_referencia) {
    return this.request.post(ROUTINE_CURVAS_V2_WORKFLOW, {
      data_referencia,
    })
  }

  async startarPrecosEventosFinanceirosWorkflow(data_referencia) {
    return this.request.post(ROUTINE_PRECOS_EVENTOS_FINANCEIROS_WORKFLOW_PATH, {
      data_referencia,
    })
  }

  async startarRelatorioTabelaCalculo(data_referencia) {
    const query = data_referencia ? `?data_referencia=${encodeURIComponent(data_referencia)}` : ""
    return this.request.get(`${ROUTINE_RELATORIO_TABELA_CALCULO_PATH}${query}`)
  }

  async getRoutinesWorkflowInfo(rotinaGrupo = "CREDIT_CALC") {
    return this.request.get(
      `${WORKFLOW_EXECUTIONS_V2_PATH}?limit=200&workflow_group=${encodeURIComponent(rotinaGrupo)}`
    )
  }

  async listWorkflowExecutions({
    page = 1,
    pageSize = 25,
    workflowGroup = "CREDIT_CALC,CADASTRO,DADOSEXTERNOS,RECEBIVEIS",
    dagId,
    status,
    contractVersion,
    periodStart,
    periodEnd,
  } = {}) {
    const query = compactQuery({
      limit: Math.max(1, Number(pageSize) || 25),
      offset: pageToOffset(page, pageSize),
      workflow_group: workflowGroup,
      dag_id: dagId,
      status,
      contract_version: contractVersion,
      period_start: periodStart,
      period_end: periodEnd,
    })
    const response = await this.request.get(`${WORKFLOW_EXECUTIONS_V2_PATH}?${query}`)
    return normalizePaginatedResponse(response)
  }

  async getRoutineWorkflowExecution(executionId) {
    return this.request.get(`${WORKFLOW_EXECUTIONS_V2_PATH}${encodeURIComponent(executionId)}/`)
  }
}
